import { useEffect, useState } from "react";
import { getUserReviews, createReview } from "apis";
import { useAppModal } from "components/common/AppModalProvider";
import { getImageUrl } from "utils/image.util";

interface ReviewSectionProps {
    userId: string;
    bookingId?: string; // if provided, user can write a review
}

interface ReviewItem {
    id: string;
    rating: number;
    comment: string;
    reviewerName: string;
    reviewerProfileImage: string;
    createdAt: string;
}

const StarDisplay = ({ rating }: { rating: number }) => (
    <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(n => (
            <span key={n} className={`text-sm ${n <= rating ? 'text-yellow-400' : 'text-achromatic-200'}`}>
                ★
            </span>
        ))}
    </div>
);

const ReviewSection = ({ userId, bookingId }: ReviewSectionProps) => {
    const [reviews, setReviews] = useState<ReviewItem[]>([]);
    const [avgRating, setAvgRating] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [showWriteForm, setShowWriteForm] = useState(false);
    const [newRating, setNewRating] = useState(5);
    const [newComment, setNewComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { showAlert } = useAppModal();

    useEffect(() => {
        loadReviews();
    }, [userId]);

    const loadReviews = async () => {
        try {
            const res = await getUserReviews(userId);
            const data = res.data.data;
            setReviews(data.reviews || []);
            setAvgRating(data.averageRating || 0);
            setTotalCount(data.totalCount || 0);
        } catch (e) {
            console.error(e);
        }
    };

    const handleSubmitReview = async () => {
        if (!bookingId || !newComment.trim()) return;
        setSubmitting(true);
        try {
            await createReview(newRating, newComment, bookingId);
            setShowWriteForm(false);
            setNewComment("");
            setNewRating(5);
            loadReviews();
        } catch (e: any) {
            showAlert(e.response?.data?.message || "리뷰 작성에 실패했습니다.", { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Summary */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="text-3xl font-extrabold text-achromatic-900">{avgRating}</div>
                    <div>
                        <StarDisplay rating={Math.round(avgRating)} />
                        <span className="text-[11px] text-achromatic-400">{totalCount}개의 리뷰</span>
                    </div>
                </div>
                {bookingId && !showWriteForm && (
                    <button
                        onClick={() => setShowWriteForm(true)}
                        className="px-4 py-2 bg-primary-500 text-white rounded-xl text-[12px] font-bold hover:bg-primary-600 transition-colors"
                    >
                        리뷰 작성
                    </button>
                )}
            </div>

            {/* Write form */}
            {showWriteForm && (
                <div className="bg-achromatic-50 rounded-xl p-4 flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <span className="text-[12px] font-bold text-achromatic-500">별점</span>
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map(n => (
                                <button key={n} onClick={() => setNewRating(n)}
                                    className={`text-xl transition-colors ${n <= newRating ? 'text-yellow-400' : 'text-achromatic-200'}`}>
                                    ★
                                </button>
                            ))}
                        </div>
                    </div>
                    <textarea
                        value={newComment}
                        onChange={e => setNewComment(e.target.value)}
                        placeholder="거래는 어떠셨나요?"
                        rows={3}
                        className="border border-achromatic-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500 resize-none"
                    />
                    <div className="flex gap-2 justify-end">
                        <button onClick={() => setShowWriteForm(false)}
                            className="px-4 py-2 text-[12px] font-bold text-achromatic-500 hover:text-achromatic-700 transition-colors">
                            취소
                        </button>
                        <button onClick={handleSubmitReview} disabled={submitting}
                            className="px-4 py-2 bg-primary-500 text-white rounded-lg text-[12px] font-bold hover:bg-primary-600 transition-colors disabled:opacity-50">
                            {submitting ? "..." : "등록"}
                        </button>
                    </div>
                </div>
            )}

            {/* Review list */}
            {reviews.length === 0 ? (
                <div className="text-center py-8 text-achromatic-300 text-sm">
                    아직 리뷰가 없습니다
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {reviews.map(review => (
                        <div key={review.id} className="flex gap-3 p-3 bg-achromatic-50 rounded-xl">
                            <div className="w-8 h-8 rounded-full bg-achromatic-200 overflow-hidden flex-shrink-0">
                                {review.reviewerProfileImage ? (
                                    <img src={getImageUrl(review.reviewerProfileImage)} alt=""
                                        className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-[11px] text-achromatic-400">👤</div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[12px] font-bold text-achromatic-700">{review.reviewerName}</span>
                                    <StarDisplay rating={review.rating} />
                                </div>
                                <p className="text-[13px] text-achromatic-600 leading-relaxed">{review.comment}</p>
                                <span className="text-[11px] text-achromatic-300 mt-1 block">
                                    {new Date(review.createdAt).toLocaleDateString('ko-KR')}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReviewSection;
